// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient, ProjectMembership } from '@medplum/fhirtypes';
import { IconClipboardList, IconHistory, IconMessageCircle } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { createElement } from 'react';
import type { Role } from '../../utils/permissions';

export function patientPathPrefix(patientId: string): string {
  return `/Patient/${patientId}`;
}

export function prependPatientPath(patient: Patient | undefined, path: string): string {
  if (patient?.id) {
    return `${patientPathPrefix(patient.id)}${!path.startsWith('/') ? '/' : ''}${path}`;
  }

  return path;
}

export function formatPatientPageTabUrl(patientId: string, tab: PatientPageTabInfo): string {
  return `${patientPathPrefix(patientId)}/${tab.url.replace('%patient.id', patientId)}`;
}

export type PatientPageTabInfo = {
  id: string;
  url: string;
  label: string;
  icon?: ReactNode;
};

export function getPatientPageTabOrThrow(tabId: string): PatientPageTabInfo {
  const result = PatientPageTabs.find((tab) => tab.id === tabId);

  if (!result) {
    throw new Error(`Could not find patient page tab with id ${tabId}`);
  }
  return result;
}

export function getPatientPageTabs(
  _membership: ProjectMembership | undefined,
  role: Role | undefined
): PatientPageTabInfo[] {
  if (role === 'clinical-ops-rcm') {
    return PatientPageTabs.filter((t) => t.id === 'profile' || t.id === 'appointment');
  }
  return PatientPageTabs;
}

const messageIcon = (): JSX.Element => createElement(IconMessageCircle, { size: 14 });
const commsLogIcon = (): JSX.Element => createElement(IconHistory, { size: 14 });

export const PatientPageTabs: PatientPageTabInfo[] = [
  { id: 'profile', url: 'profile', label: 'Profile' },
  { id: 'medical-info', url: 'medical-info', label: 'Medical Info' },
  { id: 'appointment', url: 'Appointment', label: 'Appointments' },
  { id: 'devices', url: 'devices', label: 'Devices' },
  { id: 'orders', url: 'orders', label: 'Orders', icon: createElement(IconClipboardList, { size: 14 }) },
  { id: 'comms-log', url: 'comms-log', label: 'Comms Log', icon: commsLogIcon() },
  { id: 'message', url: 'Communication', label: 'Message', icon: messageIcon() },
];
